import cn from 'classnames';
import { IconArrow } from '@/assets/icons/IconArrow';
import { IconWarningTriangle } from '@/assets/icons/IconWarningTriangle';
import styles from './checklistitem.module.scss';

const MOBILE_STORE_HREF = 'https://sxm.app.link/SiriusXMStartListeningUS';

const IconCheck = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="10" fill="#6DD69B" />
        <path
            d="M5.5 10.5L8.5 13.5L14.5 7.5"
            stroke="#0F1113"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export interface ChecklistItemWarning {
    /**
     * Full label content. Inline markup (e.g., underlined action verb) is the
     * consumer's responsibility.
     */
    label: React.ReactNode;
    /**
     * When provided, the warning-state card renders as an anchor that
     * navigates to this href on click instead of marking the item checked.
     * Use when the warning resolution lives on a different page.
     */
    href?: string;
}

export interface ChecklistItemProps {
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    stepNumber: number;
    checked?: boolean;
    onClick?: () => void;
    href?: string;
    linkType?: 'app-download';
    mobileHref?: string;
    onAppDownloadClick?: () => void;
    warning?: ChecklistItemWarning;
}

export function ChecklistItem({
    icon,
    title,
    subtitle,
    stepNumber,
    checked = false,
    onClick,
    href,
    linkType,
    mobileHref,
    onAppDownloadClick,
    warning,
}: ChecklistItemProps) {
    const isWarning = !!warning && !checked;
    const className = cn(styles.card, {
        [styles.checked]: checked,
        [styles.warning]: isWarning,
    });
    const linkName = `${title} - ${isWarning ? 'warning' : checked ? 'checked' : 'unchecked'}`;

    const rightIcon = checked ? <IconCheck /> : isWarning ? <IconWarningTriangle /> : <IconArrow />;

    const inner = (
        <>
            <div className={styles.left}>
                <span className={styles.stepNumber}>{stepNumber}</span>
                <div className={styles.iconWrapper}>{icon}</div>
                <div className={styles.textItems}>
                    <span className={styles.title}>{title}</span>
                    {isWarning ? (
                        <span className={styles.warningLabel}>{warning!.label}</span>
                    ) : (
                        subtitle && <span className={styles.subtitle}>{subtitle}</span>
                    )}
                </div>
            </div>
            <div className={styles.chevron} aria-hidden="true">
                {rightIcon}
            </div>
        </>
    );

    if (isWarning) {
        if (warning!.href) {
            return (
                <a
                    href={warning!.href}
                    target="_blank"
                    className={className}
                    data-linkname={linkName}
                    onClick={onClick}
                >
                    {inner}
                </a>
            );
        }
        return (
            <button
                type="button"
                className={className}
                data-linkname={linkName}
                onClick={onClick}
            >
                {inner}
            </button>
        );
    }

    if (href) {
        return (
            <a
                href={href}
                target="_blank"
                className={className}
                data-linkname={linkName}
                onClick={onClick}
            >
                {inner}
                {checked && <span className="show-for-sr">Completed</span>}
            </a>
        );
    }

    // Fix #5: merged app-download and plain button into one path
    return (
        <button
            type="button"
            className={className}
            data-linkname={linkName}
            aria-pressed={checked}
            aria-disabled={checked}
            onClick={() => {
                if (linkType === 'app-download') {
                    onClick?.();
                    if (window.matchMedia('(min-width: 1024px)').matches) {
                        onAppDownloadClick?.();
                    } else {
                        window.location.href = mobileHref ?? MOBILE_STORE_HREF;
                    }
                    return;
                }
                if (checked) return;
                onClick?.();
            }}
        >
            {inner}
        </button>
    );
}
